/**
 * NotificationToastContainer Component
 *
 * Container component that manages multiple notification toasts.
 * Registers with WebSocket hook to show toasts for new notifications.
 *
 * Features:
 * - Manages toast lifecycle (add, remove)
 * - Stacks multiple toasts vertically
 * - Registers callback with useNotificationSocket hook
 * - Limits max toasts displayed (5)
 * - Auto-removes oldest toast when limit reached
 *
 * Epic: MVP 1.21.9 - Dashboard Design System & Notifications (SCRUM-459)
 *
 * Usage:
 * Add this component to your root App component or layout.
 * It will automatically show toasts when new notifications arrive.
 *
 * @example
 * <NotificationToastContainer />
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotificationSocket } from '../../hooks/notifications/useNotificationSocket';
import NotificationToast from './NotificationToast';

const MAX_TOASTS = 5; // Maximum number of toasts to show at once

/**
 * NotificationToastContainer Component
 *
 * Automatically shows toasts for new notifications via WebSocket.
 */
export default function NotificationToastContainer() {
  const [toasts, setToasts] = useState([]);
  const { registerToastCallback } = useNotificationSocket();

  // ============================================================================
  // TOAST MANAGEMENT
  // ============================================================================

  /**
   * Add new toast
   * @param {Object} notification - Notification object
   */
  const addToast = useCallback((notification) => {
    setToasts((prev) => {
      // Add new toast at the beginning
      const newToasts = [notification, ...prev];

      // Limit to MAX_TOASTS
      return newToasts.slice(0, MAX_TOASTS);
    });
  }, []);

  /**
   * Remove toast by ID
   * @param {string} notificationId - Notification ID
   */
  const removeToast = useCallback((notificationId) => {
    setToasts((prev) => prev.filter((t) => t.id !== notificationId));
  }, []);

  // ============================================================================
  // REGISTER WITH WEBSOCKET HOOK
  // ============================================================================

  useEffect(() => {
    // Register callback to show toast for new notifications
    registerToastCallback(addToast);
  }, [registerToastCallback, addToast]);

  // ============================================================================
  // RENDER
  // ============================================================================

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-0 right-0 z-50 pointer-events-none">
      <div className="pointer-events-auto">
        {toasts.map((notification, index) => (
          <NotificationToast
            key={notification.id}
            notification={notification}
            onDismiss={removeToast}
            index={index}
            autoDismissMs={5000}
          />
        ))}
      </div>
    </div>
  );
}
